import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, switchMap, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { clearApiCache } from '../state-files/action';
import { ApiService } from './api.service';
import { UserService } from './user.service';
import { storeCachedPost, StoreCachedPostDeps } from './store-cached-post';
import { storeBackedRequest } from './api-store-request';

/** Response envelope body returned by `GET /common/customer-image`. */
export interface CustomerLogoResponseBody {
  success: boolean;
  /** Path/URL of the customer's logo asset; empty/absent falls back to the default logo. */
  result: string;
  message?: string;
}

/**
 * Success body of `login/validate-user-redirection-new` (the legacy-portal hand-off).
 * `handoff_secret` is the server-issued credential the SPA vaults and presents to
 * `token/token-generate`; `user_date` is the transitional alias an older API sends
 * instead (and the current API still echoes), kept until the phase-3 cleanup in
 * `docs/superpowers/specs/2026-09-15-legacy-handoff-credential-design.md`.
 * `message` is only present on a non-200 body.
 */
export interface RedirectAuthResponse {
  role_id: number;
  user_name: string;
  user_email: string;
  user_date?: string;
  handoff_secret?: string;
  /** Base64-encoded HMAC verifier key for the redirected user's session. */
  verifier?: string;
  IsProductMappingApplicable: boolean;
  message?: string;
}

/**
 * Body of the v2 `user-services/get-user-services` response (the .NET
 * `UserAuthenticationResult`, snake_cased on the wire). Consumed by the login, redirect and
 * company-switch handlers to populate the session.
 */
export interface UserDetailsResponse {
  user_id: number;
  customer_id: number;
  customer_name: string;
  name: string;
  role: string;
  role_id: number;
  is_head: boolean;
  is_approver: boolean;
  auditor_start_period: string | null;
  auditor_end_period: string | null;
  compliance_prod_type: number;
  check_internal_applicable: number;
  check_task_applicable: number;
  check_vertical_applicable: number;
  check_label_applicable: number;
  check_event_configurable: boolean;
  is_checklist_main: number;
  is_upload_using_link_customer: boolean;
  is_certificate_visible: boolean;
  is_certificate_entity: boolean;
  is_penalty_not_display_customer: boolean;
  is_payment_customer: boolean;
  notification_dot_visible: boolean;
  message_center_dot_visible: boolean;
  tawk_hash: string;
  calendar_roles: number[];
  category_ids: number[];
  user_access_locations: string | null;
  avacom_user_id: number;
  common_user_id: number;
  common_username: string;
  save_success: boolean;
  success_message: string;
  error_messages: string | null;
}

/**
 * Injectable service that handles login, authentication, password reset, redirection, and related user account API operations.
 * @component
 * @selector LoginService
 * @example
 *   <LoginService></LoginService>
 * @input any params - Request payload used by the various authentication and account-management API methods.
 * @output Observable<HttpResponse<any>> result - Emits HTTP responses for login, verification, reset, and customization requests.
 * @method validateUser - Validates a user login request.
 * @method getUserDetails - Retrieves user details from the login service.
 * @method getCustomisationList - Loads customization data using NgRx store-backed API caching.
 * @method getCustomerLogo - Retrieves the customer logo image.
 * @method validateEmail - Verifies an email address.
 * @method verifyOtp - Verifies a one-time password using a valid token.
 * @method validateUserRedirectionNew - Validates redirection for the new login flow.
 * @method unlockAccount - Unlocks a user account.
 * @method ssoLogin - Initiates single sign-on login.
 * @method getSecurityQuestions - Fetches all security questions.
 * @method setSecurityQuestions - Submits security questions and answers.
 * @method verifySecurityQuestions - Verifies security question responses.
 * @method getRandomQuestions - Retrieves random security questions.
 * @method changePassword - Changes a password with optional reset-token handling.
 * @method verifyLinkExpiry - Checks whether a reset/login link token has expired.
 * @method resetPassword - Resets a password with optional skip-token validation.
 * @method insertUpdateAngularChoice - Inserts or updates Angular choice settings.
 * @method checkAngularChoice - Checks whether the app should redirect based on Angular choice state.
 * @method validateUserRedirectionOld - Validates old user redirection flow.
 * @method getPTTokenForCookies - Generates a token for cookies.
 * @method isDocAIVisible - Checks whether Doc AI is visible for the user.
 * @method getCustomisationListIfUserSwitchTheCompany - Fetches customization data after company switch.
 * @method checkAdfsCustomer - Validates an ADFS customer.
 * @method getBoldBiDetails - Retrieves Bold BI details.
 * @method getIdentifiersWithToken - Retrieves identifiers using a reset token for HMAC generation.
 * @lifecycle constructor - Injects HttpClient, UserService, Store, and ApiService dependencies.
 */
@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private token: string | null = null;
  private apiBaseUrl = environment.API_BASE_URL;
  private apiBaseUrlV2 = environment.API_BASE_URL_V2;
  private fetchingKeys = new Set<string>();

  constructor(private http: HttpClient, private user: UserService, private store: Store, private api: ApiService, private actions$: Actions) { }

  /**
   * Collaborators for {@link storeCachedPost}; shares this service's in-flight key set.
   * @returns The store, actions stream and in-flight cache keys.
   */
  private cachedPostDeps(): StoreCachedPostDeps {
    return { store: this.store, actions$: this.actions$, fetchingKeys: this.fetchingKeys };
  }

  /**
   * Validates a user by ensuring a valid token, generating an HMAC, and sending a signed login request.
   * @example
   * validateUser(params)
   * Observable<HttpResponse<any>>
   * @param {any} params - Request payload used to validate the user.
   * @returns {Observable<HttpResponse<any>>} Observable that emits the HTTP response from the login/signin-click request.
   */
  validateUser(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/signin-click`;
    return this.api.post(url, params);
  }

  /**
   * Retrieves user details from the login user-services endpoint using authenticated request headers.
   * @example
   * etUserDetails({ userId: 123 })
   * HttpResponse<any>
   * @param {any} params - Request payload used to generate the HMAC and sent to the API.
   * @returns An observable that emits the HTTP response whose body is the typed user-details envelope.
   */
  getUserDetails(params: any): Observable<HttpResponse<UserDetailsResponse>> {
    const url = `${this.apiBaseUrlV2}/user-services/get-user-services`;
    return this.api.post<UserDetailsResponse>(url, params);
  }

  /**
  * Fetches the customization list for a transaction using the provided parameters.
  * @example
  * etCustomisationList(params)
  * { body: responseData }
  * @param {any} params - Payload sent to the customization-list API request.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response containing the customization list data.
  **/
  getCustomisationList(params: any): Observable<HttpResponse<any>> {
    return storeCachedPost<any>(this.cachedPostDeps(), `/transaction/customization-list`, params);
  }

  /**
  * Retrieves the customer logo image from the server using authenticated request headers.
  * @example
  * etCustomerLogo(params)
  * HttpResponse<CustomerLogoResponseBody>
  * @param {any} params - Request payload containing the customer logo lookup parameters.
  * @returns {Observable<HttpResponse<CustomerLogoResponseBody>>} An observable that emits the HTTP response containing the customer logo data.
  **/
  getCustomerLogo(params: any): Observable<HttpResponse<CustomerLogoResponseBody>> {
    return storeCachedPost<CustomerLogoResponseBody>(this.cachedPostDeps(), `/common/customer-image`, params);
  }

  /**
   * Validates an email by sending the provided parameters to the email verification endpoint.
   *
   * The request stays deliberately unsigned (no Authorization / X-Payload-HMAC):
   * it is a pre-login call made before any token or verifier exists. The verifier
   * (HMAC key) is fetched by UserService after the first successful token mint.
   * See docs/HMAC_SIGNING.md.
   * @example
   * validateEmail({ email: 'user@example.com' })
   * HttpResponse<any>
   * @param {any} params - Request payload containing the email verification data (e.g. `{ email }`).
   * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response from the verification request.
   */
  validateEmail(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/common/verify-email`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
      // Remove Authorization and X-Payload-HMAC headers
    });

    return this.http.post<any>(url, params, {
      headers: headers,
      observe: 'response'
    });
  }

  /**
  * Verifies a user's one-time password (OTP) after ensuring the token is valid.
  * @example
  * erifyOtp({ user_id: 123, otp: '456789' })
  * Observable<HttpResponse<any>>
  * @param {any} params - Object containing the user_id and otp values required for OTP verification.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response from the OTP verification request.
  **/
  verifyOtp(params: any): Observable<HttpResponse<any>> {
    return this.user.ensureValidToken().pipe(
      tap(token => {
        this.token = token;
      }),
      switchMap(token => {
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
        const url = `${this.user.apiBaseUrl}/login/verifyotp?
        user_id=${params.user_id}&otp=${params.otp}`.replace(/\s+/g, '');
        return this.http.post<HttpResponse<any>>(url, {}, { headers, observe: 'response' });
      })
    );
  }

  /**
   * Redeems a legacy-portal redirect token at `login/validate-user-redirection-new`.
   * On 200 the body is a {@link RedirectAuthResponse} carrying the user's e-mail and the
   * hand-off credential the SPA vaults for every later token mint.
   * @param userid - The redirect token from the `/auth-redirect?token=` hand-off URL.
   * @param logintype - Legacy login type discriminator (0 = redirect-token flow).
   * @returns Observable emitting the full HTTP response, typed on the success body.
   */
  validateUserRedirectionNew(userid: string, logintype: number): Observable<HttpResponse<RedirectAuthResponse>> {
    const url = `${this.apiBaseUrl}/login/validate-user-redirection-new`;
    const body = {
      token: userid,
      LoginType: logintype
    };
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${userid}`,
    });
    return this.http.post<RedirectAuthResponse>(url, body, { headers: headers, observe: 'response' });
  }

  /**
  * Unlocks a user account by sending the provided parameters to the unlock-account endpoint.
  * @example
  * nlockAccount(params)
  * Observable<HttpResponse<any>>
  * @param {any} params - Request payload containing the account unlock details.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response from the unlock-account request.
  **/
  unlockAccount(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/unlock-account`;
    return this.api.post(url, params);
  }

  /**
   * Initiates SSO login by validating a token, generating an HMAC, and sending the login request.
   * @example
   * soLogin(params)
   * Observable<HttpResponse<any>>
   * @param {any} params - Login payload containing the SSO request parameters.
   * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response from the SSO login request.
   */
  ssoLogin(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/sso-login`;
    return this.api.post(url, params);
  }

  /**
   * Retrieves the list of all security questions from the login service.
   * @example
   * etSecurityQuestions()
   * Observable<HttpResponse<any>>
   * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response containing the security questions list.
   **/
  getSecurityQuestions(): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/get-all-questions-list`;
    return this.api.postEmpty(url);
  }

  /**
  * Adds security questions and answers for a user by sending an authenticated POST request.
  * @example
  * etSecurityQuestions({ question1: "What is your pet's name?", answer1: "Buddy" })
  * HttpResponse<any>
  * @param {any} params - Payload containing the security question data to submit.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response from the security questions endpoint.
  **/
  setSecurityQuestions(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/add-security-qa`;
    return this.api.postEmpty(url);
  }

  /**
   * Verifies the provided security questions using an authenticated API request.
   * @example
   * verifySecurityQuestions(params)
   * Observable<HttpResponse<any>>
   * @param {any} params - Request payload containing the security question answers and related data.
   * @returns {Observable<HttpResponse<any>>} An observable that emits the full HTTP response from the verification request.
   */
  verifySecurityQuestions(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/verify-security-qa`;
    return this.api.post(url, params);
  }
  /**
   * Retrieves random login questions by sending an authenticated request with a payload HMAC.
   * @example
   * etRandomQuestions(params)
   * Observable<HttpResponse<any>>
   * @param {any} params - Request payload used to generate the HMAC and sent to the API.
   * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response containing the random questions.
   */
  getRandomQuestions(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/get-random-questions`;
    return this.api.post(url, params);
  }

  /**
   * Changes the user's password by submitting the provided request payload, optionally bypassing token validation with a reset token.
   * @example
   * changePassword(params, true, 'reset-token-value')
   * Observable<HttpResponse<any>>
   * @param {any} params - Request payload containing the password reset data.
   * @param {boolean} skipTokenValidation - Whether to skip validating the current token and use the provided reset token instead.
   * @param {string} resetToken - Reset token to use when skipping token validation.
   * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response from the password reset request.
   **/
  changePassword(params: any, skipTokenValidation: boolean = false, resetToken: string = ''): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/reset-password-new`;

    // If skipTokenValidation is true (reset password flow with data param),
    // use the provided resetToken instead of calling ensureValidToken
    if (skipTokenValidation && resetToken) {
      const hmac = this.user.generateHmac(params, "");
      const headers = new HttpHeaders({
        'Authorization': `Bearer ${resetToken}`,
        'Content-Type': 'application/json',
        'X-Payload-HMAC': hmac,
      });

      return this.http.post<any>(url, params, {
        headers: headers,
        observe: 'response',
      });
    }

    // Regular flow with token validation
    return this.api.post(url, params);
  }

  /**
  * Verifies whether a password reset or login link token is still valid and not expired.
  * @example
  * erifyLinkExpiry("abc123token")
  * HttpResponse<any>
  * @param {string} token - The link token to validate.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response containing the verification result.
  **/
  verifyLinkExpiry(token: string): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/verify-link-expiry`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const body = {
      token: token
    };

    return this.http.post<any>(url, body, {
      headers,
      observe: 'response'
    });
  }

  /**
  * Resets a user's password using the provided parameters, with optional token validation skipping for expired-link flows.
  * @example
  * esetPassword(params, true, 'reset-token-value')
  * Observable<HttpResponse<any>>
  * @param {any} params - Request payload containing the password reset data.
  * @param {boolean} skipTokenValidation - When true, skips token validation and sends the request directly.
  * @param {string} resetToken - Optional bearer token used when token validation is skipped.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response from the password reset request.
  **/
  resetPassword(params: any, skipTokenValidation: boolean = false, resetToken: string = ''): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/forget-password-services`;

    // If skipTokenValidation is true (linked-expired flow), skip ensureValidToken
    if (skipTokenValidation) {
      const hmac = this.user.generateHmac(params, "");
      const headersConfig: any = {
        'Content-Type': 'application/json',
        'X-Payload-HMAC': hmac,
      };
      if (resetToken) {
        headersConfig['Authorization'] = `Bearer ${resetToken}`;
      }
      const headers = new HttpHeaders(headersConfig);
      return this.http.post<any>(url, params, {
        headers: headers,
        observe: 'response',
      });
    }

    // Regular flow with token validation
    return this.api.post(url, params);

  }


  /**
   * Inserts or updates the Angular choice settings for the current user.
   * @example
   * nsertUpdateAngularChoice(params)
   * Observable<HttpResponse<any>>
   * @param {any} params - Request payload containing the Angular choice data to insert or update.
   * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response from the API call.
   */
  insertUpdateAngularChoice(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/insert-update-angular-choice`;
    return this.api.post(url, params);
  }

  /**
  * Checks whether the current login flow should redirect based on the provided parameters.
  * @example
  * heckAngularChoice(params)
  * HttpResponse<any>
  * @param {any} params - Request payload used to check redirection status.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the API response wrapped in an HttpResponse object.
  **/
  checkAngularChoice(params: any): Observable<HttpResponse<any>> {
    const endpointKey = `/login/check-is-redirection`;

    return storeBackedRequest(this.store, endpointKey, params, { bodyShape: 'envelope' });
  }


  /**
  * Validates an old user redirection request by generating an authenticated token and posting the user ID payload.
  * @example
  * validateUserRedirectionOld(123)
  * Observable<HttpResponse<any>>
  * @param {number} userid - User ID to include in the redirection validation request.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response from the token-generate-old-redirect endpoint.
  **/
  validateUserRedirectionOld(userid: number): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/token-generate-old-redirect`;
    const body = {
      user_id: userid,
    };
    return this.api.post(url, body);
  }

  /**
  * Generates a token for cookies by validating the current user token and posting to the login API.
  * @example
  * etPTTokenForCookies()
  * Observable<HttpResponse<any>>
  * @returns {Observable<HttpResponse<any>>} An observable of the HTTP response returned by the token generation request.
  **/
  getPTTokenForCookies(): Observable<HttpResponse<any>> {
    const apiUrl = `${this.apiBaseUrl}/login/generate-random-token-ai`;
    return this.api.postEmpty(apiUrl);
  }

  /**
  * Checks whether Doc AI visibility is enabled by sending the provided parameters with authenticated headers.
  * @example
  * sDocAIVisible(params)
  * HttpResponse<any>
  * @param {any} params - Request payload used to generate the HMAC and send to the Doc AI visibility endpoint.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the full HTTP response from the Doc AI visibility API.
  **/
  isDocAIVisible(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/common/doc-ai-visibility`;
    return this.api.post(url, params);
  }

  /**
  * Fetches the customization list for the current user after switching companies.
  * @example
  * etCustomisationListIfUserSwitchTheCompany(params)
  * Observable<HttpResponse<any>>
  * @param {any} params - Request payload used to generate the HMAC and sent in the POST body.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the full HTTP response containing the customization list.
  **/
  getCustomisationListIfUserSwitchTheCompany(params: any): Observable<HttpResponse<any>> {
    this.store.dispatch(clearApiCache({ key: `/transaction/customization-list:${JSON.stringify(params)}` }));
    const url = `${this.apiBaseUrl}/transaction/customization-list`;
    return this.api.post(url, params);
  }

  /**
  * Checks whether an ADFS customer is valid by sending the provided parameters with an authenticated request.
  * @example
  * heckAdfsCustomer(params)
  * Observable<HttpResponse<any>>
  * @param {any} params - Request payload containing the ADFS customer check data.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the full HTTP response from the ADFS customer check endpoint.
  **/
  checkAdfsCustomer(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/adfs-customer-check`;
    return this.api.post(url, params);
  }

  /**
  * Retrieves Bold BI details by sending an authenticated POST request with the provided payload.
  * @example
  * etBoldBiDetails(params)
  * Observable<HttpResponse<any>>
  * @param {any} params - Request payload used to generate the HMAC and sent to the API.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response containing the Bold BI details.
  **/
  getBoldBiDetails(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/get-bold-bi-details`;
    return this.api.post(url, params);
  }

  /**
   * Fetch identifiers (including verifier) using a reset token
   * Used in reset password flow to get the verifier key for HMAC generation
   */
  getIdentifiersWithToken(token: string): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/login/get-identifiers`;
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    return this.http.post<any>(url, {}, {
      headers: headers,
      observe: 'response',
    });
  }
}